import React from 'react'
import PropTypes from 'prop-types'
import { Navigate, useParams } from 'react-router-dom'
import _ from '@/utils/lodash'

import ImageViewer from '@components/shared/ImageViewer'
import { SpreadViewer } from '@components/shared/ImageViewer'
import NotFound from '@app/(website)/not-found'
import { useAppContext } from '@/context/AppContext'
import { findPageByPath } from '@/utils/pagePaths'
import { flattenImages, toVideoUrl } from '@components/shared/ImageViewer/imageViewerUtils'
import { isVideo } from '@/utils/media'

/**
 * Public image route.
 * Supports:
 * - /:image.img (home image)
 * - /:slug/:image.img (page image)
 * - /:slug/nested/:image.img (nested page image)
 * 
 * Images may be individual IDs or spreads (arrays of 2 images).
 * The URL always uses individual image IDs - the viewer handles
 * displaying spreads on larger screens.
 *
 * Video ids that still use a legacy `.img` URL are redirected to `.vid`.
 */
export default function PublicImageRoute({ match: matchProp }) {
    const params = useParams()
    const { content } = useAppContext()
    const match = matchProp ?? { params }

    const slug = match?.params?.slug ?? params.slug ?? null
    const imageId = match?.params?.image ?? params.image ?? null

    // Find page by full path if slug exists
    const pages = content?.pages || {}
    let pageData = null;
    if (slug) {
        pageData = findPageByPath(slug, pages);
        // Fallback to direct lookup for backward compatibility
        if (!pageData) {
            pageData = _.result(content, ['pages', slug], null);
        }
    }

    // Check gallery type - only masonry and slideshow support image URLs
    // Directory and other types should return 404 for image URLs
    const galleryType = slug && pageData
        ? (pageData.gallery?.type || pageData.gallery)
        : (content?.home?.gallery?.type || content?.home?.gallery);
    
    // Only masonry and slideshow (editorial) galleries support image URLs
    const isImageGallery = galleryType === 'masonry' || galleryType === 'slideshow';
    
    if (!isImageGallery) {
        return <NotFound />
    }

    const rawImages = slug && pageData
        ? (pageData.gallery?.images || [])
        : _.result(content, 'home.gallery.images', [])
    
    // Flatten images to check if imageId exists (spreads become individual IDs)
    const flatImages = flattenImages(rawImages)

    if (!imageId || !flatImages.includes(imageId)) {
        return <NotFound />
    }

    const file = content?.files?.[imageId]
    if (isVideo(file)) {
        return <Navigate to={toVideoUrl(slug, imageId, pages)} replace />
    }

    const imageMatch = {
        ...match,
        params: { ...match.params, slug, image: imageId },
    }

    if (galleryType === 'slideshow') {
        return <SpreadViewer match={imageMatch} />
    }
    return <ImageViewer match={imageMatch} />
}

PublicImageRoute.propTypes = {
    match: PropTypes.object,
}
